import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Calendar as CalendarIcon, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { calendarAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const holidaySchema = z.object({
  date: z.string().min(1, 'Date is required'),
  holiday_name: z.string().min(1, 'Holiday name is required'),
  holiday_type: z.enum(['national', 'saturday', 'custom']),
  is_working_day_override: z.boolean().optional(),
});

const CalendarManagement = () => {
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [currentDate, setCurrentDate] = useState('');

  const canManage = hasPermission('calendar:manage');

  const defaultValues = useMemo(() => ({
    date: '',
    holiday_name: '',
    holiday_type: 'custom',
    is_working_day_override: false,
  }), []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(holidaySchema),
    defaultValues,
  });

  useEffect(() => {
    loadCalendar();
  }, []);

  const loadCalendar = async () => {
    setLoading(true);
    try {
      const [holidayData, currentDateData] = await Promise.all([
        calendarAPI.getHolidays(),
        calendarAPI.getCurrentDate()
      ]);
      setHolidays(Array.isArray(holidayData) ? holidayData : []);
      setCurrentDate(currentDateData?.nepali_date || '');
    } catch (error) {
      toast.error('Failed to load calendar', error.response?.data?.detail || 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await loadCalendar();
      toast.success('Calendar refreshed');
    } catch (error) {
      toast.error('Failed to refresh calendar');
    } finally {
      setRefreshing(false);
    }
  };

  const onSubmit = async (data) => {
    if (!canManage) {
      toast.error('Permission denied', 'You do not have access to manage holidays');
      return;
    }
    setLoading(true);
    try {
      if (editingHoliday) {
        await calendarAPI.updateHoliday(editingHoliday.id, data);
        toast.success('Holiday updated');
      } else {
        await calendarAPI.createHoliday(data);
        toast.success('Holiday created');
      }
      setEditingHoliday(null);
      reset(defaultValues);
      await loadCalendar();
    } catch (error) {
      toast.error('Failed to save holiday', error.response?.data?.detail || 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    reset({
      date: holiday.date,
      holiday_name: holiday.holiday_name,
      holiday_type: holiday.holiday_type,
      is_working_day_override: holiday.is_working_day_override,
    });
  };

  const handleDelete = async (holiday) => {
    if (!canManage) {
      toast.error('Permission denied', 'You do not have access to manage holidays');
      return;
    }
    try {
      await calendarAPI.deleteHoliday(holiday.id);
      toast.success('Holiday deleted');
      await loadCalendar();
    } catch (error) {
      toast.error('Failed to delete holiday', error.response?.data?.detail || 'Please try again');
    }
  };

  const cancelEdit = () => {
    setEditingHoliday(null);
    reset(defaultValues);
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'national': return 'info';
      case 'saturday': return 'warning';
      default: return 'success';
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Calendar</h1>
            <p className="text-gray-600 mt-2">Manage holidays and special dates</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Badge variant="info" className="text-sm px-3 py-1">
              Current Nepali Date: {currentDate || '—'}
            </Badge>
            <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing}>
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-1"
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CalendarIcon size={18} className="mr-2" />
                {editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <Label htmlFor="date">Date (BS)</Label>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field }) => (
                      <Input
                        placeholder="YYYY-MM-DD"
                        {...field}
                        error={errors.date?.message}
                      />
                    )}
                  />
                </div>
                <div>
                  <Label htmlFor="holiday_name">Holiday Name</Label>
                  <Controller
                    name="holiday_name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        placeholder="Holiday name"
                        {...field}
                        error={errors.holiday_name?.message}
                      />
                    )}
                  />
                </div>
                <div>
                  <Label htmlFor="holiday_type">Type</Label>
                  <Controller
                    name="holiday_type"
                    control={control}
                    render={({ field }) => (
                      <Select {...field}>
                        <option value="custom">Custom</option>
                        <option value="national">National</option>
                        <option value="saturday">Saturday</option>
                      </Select>
                    )}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Controller
                    name="is_working_day_override"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                    )}
                  />
                  <span className="text-sm text-gray-700">Working day override</span>
                </div>
                <div className="flex space-x-3">
                  <Button type="submit" className="flex-1" disabled={!canManage} loading={loading}>
                    {editingHoliday ? 'Update' : 'Create'} Holiday
                  </Button>
                  {editingHoliday && (
                    <Button type="button" variant="outline" onClick={cancelEdit}>
                      Cancel
                    </Button>
                  )}
                </div>
                {!canManage && (
                  <p className="text-xs text-amber-600">
                    You have view-only access to the calendar.
                  </p>
                )}
              </form>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle>Holidays</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center text-gray-500 py-8">Loading holidays...</div>
              ) : holidays.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No holidays found.</div>
              ) : (
                <div className="space-y-3">
                  {holidays.map((holiday) => (
                    <div
                      key={holiday.id}
                      className="flex items-center justify-between border border-gray-200 rounded-lg p-3"
                    >
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{holiday.holiday_name}</p>
                        <p className="text-xs text-gray-500">{holiday.date}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Badge variant={getTypeBadge(holiday.holiday_type)}>
                          {holiday.holiday_type}
                        </Badge>
                        {holiday.is_working_day_override && (
                          <Badge variant="warning">Working Day</Badge>
                        )}
                        {canManage && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleEdit(holiday)}>
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(holiday)}>
                              <Trash2 size={16} />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default CalendarManagement;
